<template>
  <v-card class="mx-auto">
    <v-card-title class="text-h5 primary white--text">
      <v-icon color="white" class="mr-2">mdi-webhook</v-icon>
      {{ editing ? 'Editar Webhook' : 'Novo Webhook' }}
      <v-spacer></v-spacer>
      <v-btn icon color="white" @click="fechar">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <!-- Mensagem de erro de permissão -->
    <v-card-text v-if="(editing && !canUpdate) || (!editing && !canCreate)" class="text-center py-8">
      <div class="red--text text-h6">
        <v-icon color="red" large class="mb-4">mdi-lock</v-icon>
        <br>
        Você não tem permissão para {{ editing ? 'editar' : 'criar' }} webhooks
      </div>
    </v-card-text>

    <v-form v-if="(editing && canUpdate) || (!editing && canCreate)" ref="form" v-model="formValido" @submit.prevent="salvar">
      <v-card-text class="pt-4">
        <v-container>
          <!-- Informações Básicas -->
          <v-row>
            <v-col cols="12">
              <v-subheader class="text-h6 font-weight-bold primary--text">
                Informações Básicas
              </v-subheader>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="webhook.nome"
                label="Nome *"
                :rules="[rules.required]"
                outlined
                dense
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="webhook.type"
                :items="tiposWebhook"
                label="Tipo *"
                :rules="[rules.required]"
                outlined
                dense
                required
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="webhook.url"
                label="URL *"
                :rules="[rules.required, rules.url]"
                outlined
                dense
                required
                hint="URL completa para o webhook (ex: https://api.exemplo.com/webhook)"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="webhook.descricao"
                label="Descrição"
                outlined
                dense
                rows="3"
                hint="Descrição opcional do webhook"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Configurações -->
          <v-row>
            <v-col cols="12">
              <v-subheader class="text-h6 font-weight-bold primary--text">
                Configurações
              </v-subheader>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="webhook.http_Method"
                :items="metodosHttp"
                label="Método HTTP *"
                :rules="[rules.required]"
                outlined
                dense
                required
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="webhook.type_request"
                :items="tiposRequest"
                label="Tipo de Request *"
                :rules="[rules.required]"
                outlined
                dense
                required
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="webhook.gatilho_status"
                :items="gatilhosStatus"
                label="Status do Gatilho"
                outlined
                dense
                clearable
                hint="Status específico para o gatilho (opcional)"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-switch
                v-model="webhook.active"
                label="Webhook Ativo"
                color="success"
                inset
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Filtros e Associações -->
          <v-row>
            <v-col cols="12">
              <v-subheader class="text-h6 font-weight-bold primary--text">
                Filtros e Associações *
              </v-subheader>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="webhook.gate"
                :items="gateItems"
                label="Gate"
                item-text="trade_name"
                item-value="public_id"
                :rules="[rules.required]"
                outlined
                dense
                disabled
                required
                persistent-hint
              >
                <template v-slot:prepend-inner>
                  <v-icon color="primary">mdi-gate</v-icon>
                </template>

                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center" v-if="item">
                    <v-avatar size="24" color="primary" class="mr-2">
                      <span class="white--text text-caption">
                        {{ getInitials((typeof item === 'object' ? (item.trade_name || item.business_name) : (findFornecedor(item) && (findFornecedor(item).trade_name || findFornecedor(item).business_name))) || '') }}
                      </span>
                    </v-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-truncate font-weight-medium" style="line-height: 1.2;">
                        {{ (typeof item === 'object' ? (item.trade_name || item.business_name) : (findFornecedor(item) && (findFornecedor(item).trade_name || findFornecedor(item).business_name))) || '' }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption text--secondary" v-if="(typeof item === 'object' ? item.cnpj : (findFornecedor(item) && findFornecedor(item).cnpj))">
                        {{ formatCnpjCpf((typeof item === 'object' ? item.cnpj : (findFornecedor(item) && findFornecedor(item).cnpj)) || '') }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="webhook.fornecedor"
                :items="fornecedorItems"
                :loading="loadingFornecedores"
                :filter="filterFornecedores"
                label="Fornecedor (Opcional)"
                item-value="public_id"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-truck-delivery"
                @change="onFornecedorChange"
              >
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="24" color="primary" class="mr-2">
                      <span class="white--text text-caption">
                        {{ getInitials(item.trade_name || item.business_name) }}
                      </span>
                    </v-avatar>
                    <div>
                      <div class="text-truncate font-weight-medium">{{ item.trade_name || item.business_name }}</div>
                      <div class="text-caption text--secondary">{{ formatCnpjCpf(item.cnpj) }}</div>
                    </div>
                  </div>
                </template>

                <template v-slot:item="{ item }">
                  <v-list-item-avatar>
                    <v-avatar size="32" color="primary">
                      <span class="white--text text-caption">
                        {{ getInitials(item.trade_name || item.business_name) }}
                      </span>
                    </v-avatar>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.trade_name || item.business_name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ formatCnpjCpf(item.cnpj) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="webhook.terminal"
                :items="terminalItems"
                :loading="loadingTerminais"
                label="Terminal (Opcional)"
                item-text="trade_name"
                item-value="public_id"
                outlined
                dense
                clearable
                :hint="webhook.fornecedor ? 'Terminais do fornecedor selecionado' : 'Terminais do gate atual'"
                persistent-hint
                prepend-inner-icon="mdi-warehouse"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.trade_name || item.business_name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.business_name && item.trade_name !== item.business_name">
                      {{ item.business_name }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>

                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="primary" class="mr-2">mdi-warehouse</v-icon>
                    <div>
                      <div class="text-truncate font-weight-medium">{{ item.trade_name || item.business_name }}</div>
                    </div>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="webhook.gatilho"
                :items="gatilhos"
                :loading="loadingGatilhos"
                label="Gatilho (Etapa de Serviço)"
                item-text="nome"
                item-value="public_id"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-cog"
                no-data-text="Não há dados disponíveis"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-avatar>
                    <v-avatar size="32" color="info">
                      <v-icon white small>mdi-cog</v-icon>
                    </v-avatar>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.nome }}</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip x-small color="primary" text-color="white" class="mr-2">
                        Ordem: {{ item.ordem }}
                      </v-chip>
                      Serviço: {{ item.servico_descricao }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>

                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="24" color="info" class="mr-2">
                      <v-icon white x-small>mdi-cog</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-truncate font-weight-medium">{{ item.nome }}</div>
                      <div class="text-caption text--secondary">
                        Ordem: {{ item.ordem }} | {{ item.servico_descricao }}
                      </div>
                    </div>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-switch
                v-model="webhook.gatilho_saida"
                label="Acionar na saída da etapa"
                color="primary"
                inset
                hint="Se marcado, o webhook será acionado ao sair da etapa. Caso contrário, ao entrar."
                persistent-hint
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Credenciais -->
          <v-row>
            <v-col cols="12">
              <v-subheader class="text-h6 font-weight-bold primary--text">
                Credenciais (Opcional)
              </v-subheader>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="webhook.username"
                label="Username"
                outlined
                dense
                hint="Username para autenticação básica"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="webhook.password"
                label="Password"
                type="password"
                outlined
                dense
                hint="Password para autenticação básica"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Headers -->
          <v-row>
            <v-col cols="12">
              <v-subheader class="text-h6 font-weight-bold primary--text">
                Headers HTTP (Opcional)
              </v-subheader>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="headersText"
                label="Headers (JSON)"
                outlined
                dense
                rows="4"
                hint='Exemplo: {"Authorization": "Bearer token", "Content-Type": "application/json"}'
                persistent-hint
                :rules="[rules.json]"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Eventos -->
          <v-row>
            <v-col cols="12">
              <v-subheader class="text-h6 font-weight-bold primary--text">
                Body da Requisição (Opcional)
              </v-subheader>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="bodyText"
                label="Body"
                outlined
                dense
                rows="6"
                :hint="webhook.type_request === 'application/json' ? 'JSON válido' : 'Corpo da requisição'"
                persistent-hint
                :rules="webhook.type_request === 'application/json' ? [rules.json] : []"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Payload -->
          <v-row>
            <v-col cols="12">
              <v-subheader class="text-h6 font-weight-bold primary--text">
                Response Esperado (Opcional)
              </v-subheader>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="responseText"
                label="Response (JSON)"
                outlined
                dense
                rows="6"
                hint='Exemplo: {"key": "access_token"}'
                persistent-hint
                :rules="[rules.json]"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          type="submit"
          :loading="saving"
          :disabled="!formValido"
        >
          <v-icon left>mdi-content-save</v-icon>
          {{ editing ? 'Atualizar' : 'Salvar' }}
        </v-btn>
        <v-btn color="grey" text @click="fechar">
          Cancelar
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import WebhookApi from '@/services/webhook/webhook.service'
import GatesApi from '@/services/tenants/gates'
import FornecedorService from '@/services/tenants/fornecedor'
import EtapaServicoService from '@/services/patio/etapaServico'
import { mapActions } from 'vuex'
import { BasicPermissions, Sequences } from '@/utils/permissions'
import { customChange } from '@/mixins/autocomplete.js'

export default {
  name: 'WebhookForm',

  mixins: [customChange],

  props: {
    item: {
      type: Object,
      default: () => ({})
    },
    editing: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      formValido: false,
      saving: false,
      webhook: {
        nome: '',
        descricao: '',
        url: '',
        http_Method: 'POST',
        type: 'REQUEST',
        type_request: 'application/json',
        active: true,
        username: null,
        password: null,
        headers: null,
        body: null,
        response: null,
        gatilho_saida: false,
        gate: null,
        proprietario: null,
        fornecedor: null,
        terminal: null,
        gatilho: null,
        gatilho_status: null
      },
      // Listas para os selects
      terminais: [],
      gatilhos: [],
      fornecedores: [],
      loadingTerminais: false,
      loadingGatilhos: false,
      loadingFornecedores: false,
      gatilhoSearch: null,
      // Objetos selecionados para referência
      selectedGate: null,
      selectedProprietario: null,
      selectedFornecedor: null,
      headersText: '',
      bodyText: '',
      responseText: '',
      tiposWebhook: [
        { text: 'Autenticação', value: 'AUTH' },
        { text: 'Requisição', value: 'REQUEST' }
      ],
      tiposRequest: [
        { text: 'JSON', value: 'application/json' },
        { text: 'Form URL Encoded', value: 'x-www-form-urlencoded' },
        { text: 'Form Data', value: 'multipart/form-data' },
        { text: 'XML', value: 'application/xml' },
        { text: 'Text', value: 'text/plain' }
      ],
      metodosHttp: [
        { text: 'GET', value: 'GET' },
        { text: 'POST', value: 'POST' },
        { text: 'PUT', value: 'PUT' },
        { text: 'PATCH', value: 'PATCH' },
        { text: 'DELETE', value: 'DELETE' }
      ],
      gatilhosStatus: [
        { text: 'Cancelamento', value: 'CANCELAMENTO' }
      ],
      rules: {
        required: v => !!v || 'Campo obrigatório',
        url: v => {
          if (!v) return true
          const pattern = /^https?:\/\/.+/
          return pattern.test(v) || 'URL deve começar com http:// ou https://'
        },
        number: v => {
          if (v === null || v === undefined || v === '') return true
          return !isNaN(v) || 'Deve ser um número'
        },
        json: v => {
          if (!v) return true
          try {
            JSON.parse(v)
            return true
          } catch {
            return 'JSON inválido'
          }
        }
      }
    }
  },

  computed: {
    empresaAtual() {
      return this.$store.state.auth.empresaAtual
    },
    user() {
      return this.$store.state.auth.user
    },

    gateItems() {
      const items = []
      
      // Adicionar empresa atual
      if (this.empresaAtual) {
        items.push({
          business_name: this.empresaAtual.tenant,
          public_id: this.empresaAtual.public_id,
          cnpj: this.empresaAtual.cnpj,
          trade_name: this.empresaAtual.trade_name,
          active: this.empresaAtual.active
        })
      }
      
      // Se estiver editando e tiver gate_detail diferente da empresa atual, adicionar também
      if (this.editing && this.item.gate_detail) {
        const gateDetail = this.item.gate_detail
        const gateId = gateDetail.public_id || gateDetail.id
        const empresaId = this.empresaAtual?.public_id
        
        if (gateId !== empresaId) {
          items.push({
            business_name: gateDetail.business_name,
            public_id: gateId,
            cnpj: gateDetail.cnpj,
            trade_name: gateDetail.trade_name,
            active: gateDetail.active
          })
        }
      }
      
      return items
    },

    fornecedorItems() {
      const items = [...this.fornecedores]
      
      if (this.editing && this.item.fornecedor_detail) {
        const fornecedorDetail = this.item.fornecedor_detail
        const fornecedorId = fornecedorDetail.public_id || fornecedorDetail.id

        const exists = items.some(f => 
          (f.public_id || f.id) === fornecedorId
        )
        
        if (!exists) {
          items.unshift({
            business_name: fornecedorDetail.business_name,
            public_id: fornecedorId,
            id: fornecedorId,
            cnpj: fornecedorDetail.cnpj,
            trade_name: fornecedorDetail.trade_name,
            active: fornecedorDetail.active
          })
        }
      }
      
      return items
    },

    terminalItems() {
      const items = [...this.terminais]
      
      // Se estiver editando e tiver terminal_detail, garantir que está na lista
      if (this.editing && this.item.terminal_detail) {
        const terminalDetail = this.item.terminal_detail
        const terminalId = terminalDetail.public_id || terminalDetail.id
        
        // Verificar se já existe na lista
        const exists = items.some(t => 
          (t.public_id || t.id) === terminalId
        )
        
        if (!exists) {
          items.unshift({
            business_name: terminalDetail.business_name,
            public_id: terminalId,
            id: terminalId,
            cnpj: terminalDetail.cnpj,
            trade_name: terminalDetail.trade_name,
            active: terminalDetail.active
          })
        }
      }
      
      return items
    },
    permission() {
      return Sequences.Webhooks.toString()
    },
    canCreate() {
      return this.$canDo(BasicPermissions.CADASTRAR, this.permission)
    },
    canUpdate() {
      return this.$canDo(BasicPermissions.EDITAR, this.permission)
    }
  },

  watch: {
    item: {
      immediate: true,
      async handler(newItem) {
        if (newItem && Object.keys(newItem).length > 0) {
          this.webhook = { ...newItem }
          this.headersText = newItem.headers ? JSON.stringify(newItem.headers, null, 2) : ''
          this.bodyText = newItem.body || ''
          this.responseText = newItem.response ? JSON.stringify(newItem.response, null, 2) : ''

          if (newItem.gate_detail) {
            if (!this.webhook.gate) {
              this.webhook.gate = newItem.gate_detail.public_id || newItem.gate_detail.id
            }
            this.selectedGate = newItem.gate_detail
          }
          if (newItem.proprietario_detail) {
            if (!this.webhook.proprietario) {
              this.webhook.proprietario = newItem.proprietario_detail.public_id || newItem.proprietario_detail.id
            }
            this.selectedProprietario = newItem.proprietario_detail
          }
          if (newItem.fornecedor_detail && !this.webhook.fornecedor) {
            this.webhook.fornecedor = newItem.fornecedor_detail.public_id || newItem.fornecedor_detail.id
            this.selectedFornecedor = newItem.fornecedor_detail
          }
          if (newItem.terminal_detail && !this.webhook.terminal) {
            this.webhook.terminal = newItem.terminal_detail.public_id || newItem.terminal_detail.id
          }

          if (this.editing) {
            await this.$nextTick()
            if (this.webhook.fornecedor) {
              await this.loadTerminaisCombinados()
            } else if (this.webhook.gate) {
              await this.loadTerminaisDoGate()
            }
          }
        } else {
          this.resetForm()
        }
      }
    },
    
    empresaAtual: {
      immediate: true,
      handler(newEmpresa) {
        if (newEmpresa && !this.editing) {
          this.webhook.gate = newEmpresa.public_id
          this.webhook.proprietario = newEmpresa.public_id
          this.selectedGate = newEmpresa
          this.selectedProprietario = newEmpresa
        }
      }
    }
  },

  async created() {
    await this.loadGatilhos()

    if (!this.editing && this.empresaAtual) {
      this.webhook.gate = this.empresaAtual.public_id
      this.webhook.proprietario = this.webhook.gate
      this.selectedGate = this.empresaAtual
      this.selectedProprietario = this.empresaAtual
      await this.loadTerminaisDoGate()
    }

    await this.loadFornecedores()

    if (this.editing) {
      if (this.webhook.gate && this.webhook.fornecedor) {
        await this.loadTerminaisCombinados()
      } else if (this.webhook.gate) {
        await this.loadTerminaisDoGate()
      }
    }
  },

  methods: {
    ...mapActions('template', [
      'successMessage',
      'errorMessage'
    ]),
    
    findFornecedor(id) {
      if (!id) return null
      return this.fornecedorItems.find(f => (f.public_id || f.id) === id) || null
    },

    filterFornecedores(item, queryText) {
      if (!queryText) return true
      
      const searchText = queryText.toLowerCase()
      const tradeName = (item.trade_name || '').toLowerCase()
      const businessName = (item.business_name || '').toLowerCase()
      const cnpj = (item.cnpj || '').toLowerCase()
      
      return tradeName.includes(searchText) || 
             businessName.includes(searchText) || 
             cnpj.includes(searchText)
    },

    resetForm() {
      const gateId = this.empresaAtual ? this.empresaAtual.public_id : null
      
      this.webhook = {
        nome: '',
        descricao: '',
        url: '',
        http_Method: 'POST',
        type: 'REQUEST',
        type_request: 'application/json',
        active: true,
        username: null,
        password: null,
        headers: null,
        body: null,
        response: null,
        gatilho_saida: false,
        gate: gateId,
        proprietario: gateId,
        fornecedor: null,
        terminal: null,
        gatilho: null,
        gatilho_status: null
      }
      this.headersText = ''
      this.bodyText = ''
      this.responseText = ''
      this.terminais = []
      this.selectedGate = this.empresaAtual
      this.selectedProprietario = this.empresaAtual
      this.selectedFornecedor = null
      this.$refs.form?.resetValidation()
    },

    async searchGates(params = {}) {
      try {
        const response = await GatesApi.getByParams(params)
        return response
      } catch (error) {
        console.error('Erro ao buscar gates:', error)
        throw error
      }
    },


    async searchFornecedoresAutocomplete(event) {
      const searchTerm = event.target.value.trim()
      
      if (searchTerm.length < 3) {
        this.fornecedores = []
        return
      }

      this.loadingFornecedores = true
      try {
        const params = {
          search: searchTerm,
          page_size: 20
        }
        
        let response
        if (this.webhook.gate) {
          response = await FornecedorService.getByGate(this.webhook.gate, params)
        } else {
          response = await FornecedorService.getAll(params)
        }
        
        this.fornecedores = response.results || response || []
      } catch (error) {
        console.error('Erro ao buscar fornecedores:', error)
        this.errorMessage('Erro ao buscar fornecedores')
        this.fornecedores = []
      } finally {
        this.loadingFornecedores = false
      }
    },

    onGateSelect(gate) {
      this.selectedGate = gate

      if (gate) {
        this.webhook.gate = gate.public_id || gate.id || gate.pk || null
        this.webhook.proprietario = this.webhook.gate
        this.selectedProprietario = gate

        this.loadTerminaisDoGate()
      } else {
        this.webhook.gate = null
        this.webhook.proprietario = null
        this.selectedProprietario = null
        this.terminais = []
      }

      this.$forceUpdate()

      this.webhook.fornecedor = null
      this.webhook.terminal = null
      this.selectedFornecedor = null
    },

    onGateChange() {
      this.webhook.fornecedor = null
      this.webhook.terminal = null
      this.selectedFornecedor = null
      this.terminais = []
    },


    onFornecedorChange() {
      this.webhook.terminal = null

      if (this.webhook.fornecedor && this.webhook.gate) {
        this.loadTerminaisCombinados()
      } else {
        this.loadTerminaisDoGate()
      }
    },

    async loadTerminaisDoGate() {
      if (!this.webhook.gate) return
      
      this.loadingTerminais = true
      try {
        const response = await FornecedorService.getTerminaisGate(this.webhook.gate)
        this.terminais = response.results || []
      } catch (error) {
        console.error('Erro ao carregar terminais do gate:', error)
        this.errorMessage('Erro ao carregar terminais do gate')
      } finally {
        this.loadingTerminais = false
      }
    },

    async loadTerminaisCombinados() {
      if (!this.webhook.gate || !this.webhook.fornecedor) return
      
      this.loadingTerminais = true
      try {
        const response = await FornecedorService.getTerminaisCombinados(this.webhook.gate, this.webhook.fornecedor)
        this.terminais = response.results || []
      } catch (error) {
        console.error('Erro ao carregar terminais combinados:', error)
        this.errorMessage('Erro ao carregar terminais')
      } finally {
        this.loadingTerminais = false
      }
    },

    async loadTerminais() {
      if (!this.webhook.fornecedor) return
      
      this.loadingTerminais = true
      try {
        const response = await FornecedorService.getTerminais(this.webhook.fornecedor)
        this.terminais = response.results || []
      } catch (error) {
        console.error('Erro ao carregar terminais do fornecedor:', error)
        this.errorMessage('Erro ao carregar terminais do fornecedor')
      } finally {
        this.loadingTerminais = false
      }
    },

    async loadFornecedores() {
      if (!this.webhook.gate && !this.empresaAtual) return
      
      this.loadingFornecedores = true
      try {
        const gate = this.webhook.gate || this.empresaAtual.public_id
        const response = await FornecedorService.getByGate(gate, { page_size: 1000 })
        this.fornecedores = response.results || response || []
      } catch (error) {
        console.error('Erro ao carregar fornecedores:', error)
        this.errorMessage('Erro ao carregar fornecedores')
      } finally {
        this.loadingFornecedores = false
      }
    },

    async loadGatilhos() {
      this.loadingGatilhos = true
      try {
        const params = {
          proprietario: this.empresaAtual.public_id
        }
        const response = await EtapaServicoService.getAll(params)
        this.gatilhos = response || []
      } catch (error) {
        console.error('Erro ao carregar etapas de serviço:', error)
        this.errorMessage('Erro ao carregar etapas de serviço')
      } finally {
        this.loadingGatilhos = false
      }
    },

    fechar() {
      this.$emit('close')
    },

    async salvar() {
      try {
        if (!this.$refs.form.validate()) return

        this.saving = true
        
        const payload = { ...(this.webhook || {}) }

        if (payload.gate && typeof payload.gate === 'object') {
          payload.gate = payload.gate.public_id
        }
        
        if (payload.proprietario && typeof payload.proprietario === 'object') {
          payload.proprietario = payload.proprietario.public_id
        }
        
        if (payload.fornecedor && typeof payload.fornecedor === 'object') {
          payload.fornecedor = payload.fornecedor.public_id
        }
        
        if (payload.terminal && typeof payload.terminal === 'object') {
          payload.terminal = payload.terminal.public_id
        }
        
        if (payload.gatilho && typeof payload.gatilho === 'object') {
          payload.gatilho = payload.gatilho.public_id
        }

        if (this.headersText && this.headersText.trim()) {
          try {
            payload.headers = JSON.parse(this.headersText)
          } catch (parseError) {
            this.errorMessage('Headers JSON inválido')
            this.saving = false
            return
          }
        } else {
          payload.headers = {}
        }

        if (this.bodyText && this.bodyText.trim()) {
          if (this.webhook.type_request === 'application/json') {
            try {
              JSON.parse(this.bodyText)
              payload.body = this.bodyText
            } catch (parseError) {
              this.errorMessage('Body JSON inválido')
              this.saving = false
              return
            }
          } else {
            payload.body = this.bodyText
          }
        } else {
          payload.body = ''
        }

        if (this.responseText && this.responseText.trim()) {
          try {
            payload.response = JSON.parse(this.responseText)
          } catch (parseError) {
            this.errorMessage('Response JSON inválido')
            this.saving = false
            return
          }
        } else {
          payload.response = {}
        }

        if (!payload.gate) {
          this.errorMessage('Gate é obrigatório')
          this.saving = false
          return
        }
        
        if (!payload.proprietario) {
          this.errorMessage('Proprietário é obrigatório')
          this.saving = false
          return
        }

        const colaboradorId = this.empresaAtual?.colaborador_id
        if (!colaboradorId) {
          this.errorMessage('Colaborador não identificado')
          this.saving = false
          return
        }

        let response
        if (this.editing) {
          response = await WebhookApi.update(colaboradorId, payload.public_id, payload)
        } else {
          response = await WebhookApi.create(colaboradorId, payload)
        }

        this.successMessage(
          this.editing ? 'Webhook atualizado com sucesso' : 'Webhook criado com sucesso'
        )

        this.$emit('saved', response)
        this.fechar()
      } catch (err) {
        let errorMessage = 'Erro ao salvar webhook'
        if (err.response?.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data
          } else if (err.response.data.detail) {
            errorMessage = err.response.data.detail
          } else {
            const errors = []
            Object.keys(err.response.data).forEach(field => {
              const fieldErrors = err.response.data[field]
              if (Array.isArray(fieldErrors)) {
                errors.push(`${field}: ${fieldErrors.join(', ')}`)
              } else {
                errors.push(`${field}: ${fieldErrors}`)
              }
            })
            errorMessage = errors.join('\n')
          }
        }
        
        this.errorMessage(errorMessage)
      } finally {
        this.saving = false
      }
    },

    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .slice(0, 2)
        .map(word => word.charAt(0).toUpperCase())
        .join('')
    },

    formatCnpjCpf(document) {
      if (!document) return ''

      const cleanDoc = document.replace(/\D/g, '')
      
      if (cleanDoc.length === 14) {
        return cleanDoc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
      } else if (cleanDoc.length === 11) {
        return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      }
      
      return document
    }
  }
}
</script>

<style scoped>
.v-subheader {
  padding-left: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
}
</style>